import { apiFetch } from '../../utils/apiFetch';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  CpuChipIcon,
  CircleStackIcon,
  ServerIcon,
  ClockIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

interface PerformanceOverview {
  health_score: number;
  health_status: string;
  components: any;
  latest_metrics: any;
  alerts: Array<{
    type: string;
    message: string;
    severity: string;
  }>;
}

interface AnalyticsData {
  period: string;
  system_metrics: any;
  database_metrics: any;
  redis_metrics: any;
  laravel_metrics: any;
  frontend_metrics: any;
}

const PerformanceAnalytics: React.FC = () => {
  const [overview, setOverview] = useState<PerformanceOverview | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [period, setPeriod] = useState('24h');
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPerformanceData();
    const interval = setInterval(fetchPerformanceData, 30000);
    return () => clearInterval(interval);
  }, [period]);

  const fetchPerformanceData = async () => {
    try {
      setRefreshing(true);
      setError(null);
      
      // Fetch overview
      const overviewResponse = await apiFetch('/api/admin/performance/overview', {
        headers: {
          'X-Tenant-ID': '1'
        }
      });
      
      if (overviewResponse.ok) {
        const overviewData = await overviewResponse.json();
        console.log('Overview data:', overviewData);
        setOverview(overviewData.overview);
      } else {
        throw new Error(`Overview API failed: ${overviewResponse.status}`);
      }

      // Fetch analytics
      const analyticsResponse = await apiFetch(`/api/admin/performance/dashboard?period=${period}`, {
        headers: {
          'X-Tenant-ID': '1'
        }
      });
      
      if (analyticsResponse.ok) {
        const analyticsData = await analyticsResponse.json();
        console.log('Analytics data:', analyticsData);
        setAnalytics(analyticsData.data);
      } else {
        throw new Error(`Analytics API failed: ${analyticsResponse.status}`);
      }

    } catch (error) {
      console.error('Failed to fetch performance data:', error);
      setError(error instanceof Error ? error.message : 'Unknown error');
      toast.error('Failed to load performance data');
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  };

  const collectMetrics = async () => {
    try {
      setRefreshing(true);
      const response = await apiFetch('/api/admin/performance/collect', {
        method: 'POST',
        headers: {
          'X-Tenant-ID': '1'
        }
      });
      
      if (response.ok) {
        toast.success('Metrics collected successfully');
        fetchPerformanceData();
      } else {
        throw new Error('Failed to collect metrics');
      }
    } catch (error) {
      console.error('Failed to collect metrics:', error);
      toast.error('Failed to collect metrics');
    } finally {
      setRefreshing(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';
      case 'warning':
        return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20';
      case 'critical':
        return 'text-red-400 bg-red-500/10 border-red-500/20';
      default:
        return 'text-slate-400 bg-slate-500/10 border-slate-500/20';
    }
  };

  const getHealthIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircleIcon className="w-6 h-6 text-emerald-400" />;
      case 'warning':
        return <ExclamationTriangleIcon className="w-6 h-6 text-yellow-400" />;
      case 'critical':
        return <ExclamationTriangleIcon className="w-6 h-6 text-red-400" />;
      default:
        return <ClockIcon className="w-6 h-6 text-slate-400" />;
    }
  };

  const formatMetricValue = (value: any, suffix = '') => {
    if (value === null || value === undefined) return 'N/A';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return 'N/A';
    if (suffix === '%') return `${numValue.toFixed(1)}${suffix}`;
    if (suffix === 'ms') return `${numValue.toFixed(0)}${suffix}`;
    return `${numValue.toFixed(1)}${suffix}`;
  };

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <ExclamationTriangleIcon className="w-6 h-6 text-red-400" />
            <div>
              <h2 className="text-lg font-semibold text-red-400">Error Loading Performance Data</h2>
              <p className="text-red-300 mt-1">{error}</p>
            </div>
          </div>
          <button
            onClick={() => {
              setError(null);
              fetchPerformanceData();
            }}
            className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-slate-700/50 rounded-xl w-1/4"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-slate-800/50 rounded-xl"></div>
          ))}
        </div>
        <div className="h-64 bg-slate-800/50 rounded-xl"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Performance Analytics</h1>
            <p className="text-slate-400">
              Real-time monitoring and performance insights
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex space-x-2">
              {['1h', '24h', '7d'].map((p) => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-all duration-200 ${
                    period === p
                      ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                      : 'bg-slate-700/50 text-slate-300 border border-slate-600/50 hover:bg-slate-600/50 hover:text-white'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
            <button
              onClick={collectMetrics}
              disabled={refreshing}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-slate-600 disabled:opacity-50 text-white rounded-lg transition-colors flex items-center space-x-2"
            >
              <ArrowPathIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span>Collect Metrics</span>
            </button>
          </div>
        </div>
      </div>

      {/* Overall Health Score */}
      {overview && (
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {getHealthIcon(overview.health_status)}
              <div>
                <h2 className="text-xl font-semibold text-white">System Health</h2>
                <div className={`text-sm px-3 py-1 rounded-full border inline-block ${getStatusColor(overview.health_status)}`}>
                  {overview.health_status.charAt(0).toUpperCase() + overview.health_status.slice(1)}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-white">{overview.health_score}</div>
              <div className="text-sm text-slate-400">Health Score</div>
            </div>
          </div>
        </div>
      )}

      {/* Component Health Cards */}
      {overview && overview.components && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {Object.entries(overview.components).map(([componentType, component]: [string, any]) => {
            const iconMap = {
              system: CpuChipIcon,
              database: CircleStackIcon,
              redis: CircleStackIcon,
              laravel: ServerIcon,
              react: ChartBarIcon
            };
            const Icon = iconMap[componentType as keyof typeof iconMap] || CpuChipIcon;

            return (
              <div key={componentType} className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <Icon className="w-6 h-6 text-blue-400" />
                  <div className={`text-xs px-2 py-1 rounded-full border ${getStatusColor(component.status)}`}>
                    {component.status}
                  </div>
                </div>
                <h3 className="font-semibold text-white text-sm mb-2 capitalize">{componentType}</h3>
                <div className="text-2xl font-bold text-white mb-1">{component.score}</div>
                <div className="text-xs text-slate-400">Health Score</div>
                
                {/* Key metrics */}
                <div className="mt-3 space-y-1">
                  {componentType === 'system' && component.metrics && (
                    <>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">CPU:</span>
                        <span className="text-slate-300">{formatMetricValue(component.metrics.cpu, '%')}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Memory:</span>
                        <span className="text-slate-300">{formatMetricValue(component.metrics.memory, '%')}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Load:</span>
                        <span className="text-slate-300">{formatMetricValue(component.metrics.load)}</span>
                      </div>
                    </>
                  )}
                  {componentType === 'laravel' && component.metrics && (
                    <>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Response:</span>
                        <span className="text-slate-300">{formatMetricValue(component.metrics.response_time, 'ms')}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Failed Jobs:</span>
                        <span className="text-slate-300">{formatMetricValue(component.metrics.failed_jobs)}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Performance Alerts */}
      {overview && overview.alerts && overview.alerts.length > 0 && (
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Performance Alerts</h3>
          <div className="space-y-3">
            {overview.alerts.map((alert, index) => (
              <div key={index} className={`p-3 rounded-lg border ${
                alert.severity === 'critical' ? 'border-red-500/20 bg-red-500/10' : 'border-yellow-500/20 bg-yellow-500/10'
              }`}>
                <div className="flex items-center space-x-3">
                  <ExclamationTriangleIcon className={`w-5 h-5 ${
                    alert.severity === 'critical' ? 'text-red-400' : 'text-yellow-400'
                  }`} />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-white font-medium capitalize">{alert.type}</span>
                      <span className={`text-xs px-2 py-1 rounded ${
                        alert.severity === 'critical' ? 'bg-red-500/20 text-red-300' : 'bg-yellow-500/20 text-yellow-300'
                      }`}>
                        {alert.severity}
                      </span>
                    </div>
                    <p className="text-slate-300 text-sm mt-1">{alert.message}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Analytics Summary */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6">
            <h4 className="text-lg font-semibold text-white mb-4">System Performance</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-slate-400">Avg CPU Usage:</span>
                <span className="text-white font-medium">{formatMetricValue(analytics.system_metrics?.avg_cpu, '%')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Avg Memory Usage:</span>
                <span className="text-white font-medium">{formatMetricValue(analytics.system_metrics?.avg_memory, '%')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Avg Load:</span>
                <span className="text-white font-medium">{formatMetricValue(analytics.system_metrics?.avg_load)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Data Points:</span>
                <span className="text-white font-medium">{analytics.system_metrics?.data_points || 0}</span>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6">
            <h4 className="text-lg font-semibold text-white mb-4">Database Performance</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-slate-400">Avg Query Time:</span>
                <span className="text-white font-medium">{formatMetricValue(analytics.database_metrics?.avg_query_time, 'ms')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Avg Connections:</span>
                <span className="text-white font-medium">{formatMetricValue(analytics.database_metrics?.avg_connections)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Data Points:</span>
                <span className="text-white font-medium">{analytics.database_metrics?.data_points || 0}</span>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6">
            <h4 className="text-lg font-semibold text-white mb-4">Application Performance</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-slate-400">Laravel Avg Response:</span>
                <span className="text-white font-medium">{formatMetricValue(analytics.laravel_metrics?.avg_response_time, 'ms')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Frontend Uptime:</span>
                <span className="text-white font-medium">{formatMetricValue(analytics.frontend_metrics?.uptime_percent, '%')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Failed Jobs Avg:</span>
                <span className="text-white font-medium">{formatMetricValue(analytics.laravel_metrics?.avg_failed_jobs)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Debug Info */}
      {process.env.NODE_ENV === 'development' && (
        <div className="bg-slate-900/50 border border-slate-700/50 rounded-xl p-4">
          <h4 className="text-sm font-semibold text-white mb-2">Debug Info</h4>
          <div className="text-xs text-slate-400 space-y-1">
            <div>Period: {period}</div>
            <div>Overview loaded: {overview ? 'Yes' : 'No'}</div>
            <div>Analytics loaded: {analytics ? 'Yes' : 'No'}</div>
            <div>Refreshing: {refreshing ? 'Yes' : 'No'}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceAnalytics;